import { groupBy, map } from 'lodash';
import { CardDetails } from '~reducers';
import { i18nProps, TranslatedString } from '~Types';
import { LINE_LENGTH } from './constants';
import { GenerateTemplateProps, ReceiptType } from './types';
import { ReceiptItem } from '@baemingo/receipt';

const formatLineWithValue = (title: string, value: string): string => {
  const valueLen = value.length;
  const whiteSpaceLen = LINE_LENGTH - valueLen - title.length;
  if (whiteSpaceLen < 0) return title;

  const whiteSpace = Array(whiteSpaceLen + 1).join(' ');
  return title + whiteSpace + value;
};

const formatLineWithBlankSpace = (text: string): string => {
  const blankSpaceLen = 16;
  const whiteSpaceLen = LINE_LENGTH - blankSpaceLen - text.length;
  if (whiteSpaceLen < 0) return text;

  const whiteSpace = Array(whiteSpaceLen + 1).join(' ');
  const blanKSpace = Array(Math.floor(blankSpaceLen / 2) + 1).join(' _');
  return text + whiteSpace + blanKSpace;
};

const generateInvoiceLines = (totalInvoiceAmountText: string, i18n: i18nProps) => {
  return [
    {
      text: ' ',
      params: {
        fill: true,
        condensed: true,
      },
    },
    {
      text: formatLineWithBlankSpace(`${i18n('Receipt.Types.Invoice')}:`),
      params: {
        bold: true,
        condensed: true,
      },
    },
    {
      text: ' ',
      params: {
        fill: true,
      },
    },
    {
      text: formatLineWithBlankSpace(`${i18n('General.Company')}:`),
      params: {
        bold: true,
        condensed: true,
      },
    },
    {
      text: ' ',
      params: {
        fill: true,
      },
    },
    {
      text: formatLineWithBlankSpace(`${i18n('Receipt.VATNo')}:`),
      params: {
        bold: true,
        condensed: true,
      },
    },
    {
      text: ' ',
      params: {
        fill: true,
      },
    },
    {
      text: formatLineWithBlankSpace('ATT:'),
      params: {
        bold: true,
        condensed: true,
      },
    },
    {
      text: ' ',
      params: {
        fill: true,
      },
    },
    {
      text: formatLineWithBlankSpace(`${i18n('Receipt.InvoiceAddress')}:`),
      params: {
        bold: true,
        condensed: true,
      },
    },
    {
      text: ' ',
      params: {
        fill: true,
      },
    },
    {
      text: formatLineWithValue(`${i18n('Receipt.InvoiceAmount')}:`, totalInvoiceAmountText),
      params: {
        bold: true,
        condensed: true,
      },
    },
    {
      text: ' ',
      params: {
        fill: true,
      },
    },
    {
      text: formatLineWithBlankSpace(`${i18n('Receipt.Tip')}:`),
      params: {
        bold: true,
        condensed: true,
      },
    },
    {
      text: ' ',
      params: {
        fill: true,
      },
    },
    {
      text: formatLineWithBlankSpace(`${i18n('Receipt.TotalAmount')}:`),
      params: {
        bold: true,
        condensed: true,
      },
    },
    {
      text: ' ',
      params: {
        fill: true,
      },
    },
    {
      text: ' ',
      params: {
        fill: true,
      },
    },
    {
      text: formatLineWithBlankSpace(`${i18n('Receipt.Signature')}:`),
      params: {
        bold: true,
        condensed: true,
      },
    },
    {
      text: ' ',
      params: {
        fill: true,
      },
    },
    {
      text: formatLineWithBlankSpace(`${i18n('Receipt.NameClarification')}:`),
      params: {
        bold: true,
        condensed: true,
      },
    },
    {
      text: ' ',
      params: {
        fill: true,
      },
    },
    {
      text: formatLineWithBlankSpace(`${i18n('Receipt.IDCheck')}:`),
      params: {
        bold: true,
        condensed: true,
      },
    },
    {
      text: ' ',
      params: {
        fill: true,
      },
    },
    {
      text: ' ',
      params: {
        fill: true,
      },
    },
    {
      text: '-',
      params: {
        fill: true,
        condensed: true,
      },
    },
  ];
};

const generateVatLines = (items: ReceiptItem[]) => {
  const vatsRows = map(
    groupBy(items, (item) => item.percentageVAT),
    (itemGroup) => {
      const total = itemGroup.reduce((sum, item) => sum + item.amount - item.discount, 0) / 100;
      const net =
        itemGroup.reduce((sum, item) => {
          const price = item.amount - item.discount;
          return sum + price / (1 + item.percentageVAT / 100);
        }, 0) / 100;
      const oneItem = itemGroup[0];

      return {
        vat: oneItem.percentageVAT,
        total,
        net,
        vatAmount: total - net,
      };
    },
  );

  return vatsRows.map((vatRow) => {
    const vat = vatRow?.vat || 0;
    const net = vatRow?.net || 0;
    const total = vatRow?.total || 0;
    const vatAmount = vatRow?.vatAmount || 0;
    return {
      text: `${vat.toString().padStart(8 - 1, ' ')}%${total
        .toFixed(2)
        .padStart(8, ' ')}${net.toFixed(2).padStart(8, ' ')}${vatAmount
        .toFixed(2)
        .padStart(8, ' ')}`,
      params: {
        condensed: true,
      },
    };
  });
};

const generateCardDetailLines = (cardDetails: CardDetails, i18n: i18nProps) => {
  if (!cardDetails) {
    return [];
  }

  const lines: any = [];
  Object.entries(cardDetails).map(([key, value]) => {
    if (key === '__typename' || !value) {
      return;
    }

    if (key === 'cardType') {
      value = `${i18n('Receipt.CardType')}: ${value}`;
    }

    if (['tsi', 'tvr', 'aid'].includes(key) && value) {
      value = `${key.toUpperCase()}: ${value}`;
    }

    lines.push({
      text: value || ' ',
      params: {
        bold: true,
        condensed: true,
        fontSize: 10,
      },
    });
  });

  return [
    {
      text: ' ',
      params: {
        fill: true,
      },
    },
    ...lines,
    {
      text: ' ',
      params: {
        fill: true,
      },
    },
  ];
};

const formatLineBreaks = (value: string, width = LINE_LENGTH) => {
  const words = value.split(' ');
  if (words.length <= 1) {
    return [value];
  }

  const lines: string[] = [];
  let linesIndex = 0;

  words.forEach((word) => {
    const lineValue = lines[linesIndex];
    const newLineValue = lineValue ? `${lineValue} ${word}` : word;
    if (newLineValue.length > width) {
      linesIndex++;
      lines[linesIndex] = word;
      return;
    }

    lines[linesIndex] = newLineValue;
  });

  return lines;
};

const mapLines = (lines: string[]) => {
  return lines
    .reduce<string[]>((result, line) => {
      return [...result, ...formatLineBreaks(line)];
    }, [])
    .map((line: string) => ({
      text: line || ' ',
      params: {
        align: 'CENTER',
        condensed: true,
      },
    }));
};

const conditionalLines = (lines: any[], condition: boolean) => {
  return condition ? lines : [];
};

export const generateTemplate: GenerateTemplateProps = ({
  type,
  payments,
  cardDetails,
  vars,
  isTable,
  receiptTemplate,
  isTypeInvoice = false,
  items = [],
  i18n,
  isCopy = false,
  country,
  showTipSuggestions,
  showStaffName,
  totalAmountLargeFont,
  staff,
}) => {
  const receiptTypes = {
    [ReceiptType.Copy]: i18n('Receipt.Types.Copy'),
    [ReceiptType.Refund]: i18n('Receipt.Types.Refund'),
    [ReceiptType.Sale]: i18n('Receipt.Types.Sale'),
    [ReceiptType.Cheque]: i18n('Receipt.Types.Cheque'),
    [ReceiptType.Invoice]: i18n('Receipt.Types.Invoice'),
    [ReceiptType.TipRefund]: i18n('Receipt.Types.TipRefund'),
  };

  // only used if type is an invoice bill
  const itemsAmount =
    items?.reduce((total, item) => total + item?.amount - item?.discount, 0) || 0;
  const totalAmount = (itemsAmount / 100).toFixed(2) || '';

  // Calculate tip amounts based on itemsAmount
  const tipFivePercent = ((itemsAmount * 1.05) / 100).toFixed(2);
  const tipTenPercent = ((itemsAmount * 1.1) / 100).toFixed(2);
  const tipFifteenPercent = ((itemsAmount * 1.15) / 100).toFixed(2);
  const tipTwentyPercent = ((itemsAmount * 1.2) / 100).toFixed(2);

  const introLines = receiptTemplate?.introLines ?? [];
  const bottomLines = receiptTemplate?.bottomLines ?? [];

  // following variables is for the formatting of the receipt title for Norway, to prevent it from being too long
  const isNorway = country === 'NO';
  const isNorwayCheque = type === ReceiptType.Cheque && isNorway;
  const receiptTypeText = receiptTypes[type].toUpperCase();
  const isCopyText =
    isCopy && type !== ReceiptType.Copy ? i18n('Receipt.Types.Copy').toUpperCase() + ' ' : '';
  const stars = isNorway ? '' : '***';

  const receiptTitleAlign = isNorwayCheque ? 'LEFT' : 'CENTER';
  const receiptTitleFontSize = isNorwayCheque ? 18 : undefined;

  // For formatting purposes in Norway, we split them into to rows.
  const receiptTitle = isNorwayCheque
    ? 'Foreløpig kvittering'
    : `${stars} ${receiptTypeText} ${isCopyText}${stars}`;
  const receiptTitlePartTwo = 'IKKE KVITTERING FOR KJØP';

  // Used specifically in the canary islands
  const useIgic = items.some((item) => item.percentageVAT === 7) && country === 'ES';
  const vatTitle = useIgic ? 'IGIC' : i18n('Receipt.VAT');

  const middleSection = [
    {
      itemList: true,
      params: {
        condensed: true,
      },
    },
    {
      text: ' ',
      params: {
        fill: true,
      },
    },
    {
      itemsTotal: true,
      text: 'Tot ${currency}',
      params: {
        bold: true,
        align: 'RIGHT',
        doubleHeigth: totalAmountLargeFont ? true : undefined,
      },
    },
    ...conditionalLines(
      [
        {
          text: `${i18n('Receipt.Discount')}: \${totalDiscount}`,
          params: {
            bold: true,
            align: 'RIGHT',
            condensed: true,
          },
        },
      ],
      !!vars['totalDiscount'] && parseFloat(vars['totalDiscount']) > 0,
    ),
    ...conditionalLines(
      [
        {
          text: ' ',
          params: {
            fill: true,
          },
        },
        {
          text: `   ${i18n('TM.Tip')}      ${i18n('Receipt.NewTotal')}`,
          params: {
            bold: true,
            align: 'CENTER',
            condensed: true,
          },
        },
        {
          text: `   5%  ->   ${tipFivePercent}` + ' ${currency}',
          params: {
            align: 'CENTER',
            condensed: true,
          },
        },
        {
          text: `   10% ->   ${tipTenPercent}` + ' ${currency}',
          params: {
            align: 'CENTER',
            condensed: true,
          },
        },
        {
          text: `   15% ->   ${tipFifteenPercent}` + ' ${currency}',
          params: {
            align: 'CENTER',
            condensed: true,
          },
        },
        {
          text: `   20% ->   ${tipTwentyPercent}` + ' ${currency}',
          params: {
            align: 'CENTER',
            condensed: true,
          },
        },
      ],
      type === ReceiptType.Cheque && Boolean(showTipSuggestions),
    ),
    {
      text: ' ',
      params: {
        fill: true,
      },
    },
    ...conditionalLines(
      [
        {
          text: `${`${vatTitle} %`.padStart(8.5, ' ')}${i18n('Receipt.Total').padStart(
            8.5,
            ' ',
          )}${i18n('Receipt.Net').padStart(8.5, ' ')}${vatTitle.padStart(8.5, ' ')}`,
          params: {
            condensed: true,
          },
        },
        ...generateVatLines(items),
      ],
      /** Hide the VAT table on cheques, except in Spain (spanish market standard) */
      type !== ReceiptType.Cheque || country === 'ES',
    ),
    {
      text: ' ',
      params: {
        fill: true,
      },
    },
  ];

  const paymentLines = [
    ...conditionalLines(
      payments
        ? [
            ...payments.map((_, index) => ({
              text: `${payments[index].amount} ${
                payments[index].tip
                  ? `(${i18n('Receipt.Including')} ${payments[index].tip} ${i18n('Receipt.tip')})`
                  : ''
              } < ${
                ['ADYEN', 'ZETTLE', 'IZETTLE', 'LOOMIS'].includes(payments[index].type)
                  ? i18n('Receipt.Card')
                  : payments[index].type
              }`,
              params: {
                bold: true,
                condensed: true,
              },
            })),
          ]
        : [],
      Boolean(payments),
    ),
    {
      text: ' ',
      params: {
        fill: true,
      },
    },
    ...conditionalLines(
      [
        {
          text: `${type === ReceiptType.Refund ? '-' : ''}${(Number(vars['tip']) / 100).toFixed(
            2,
          )}       ${i18n('Receipt.TotalTip')}`,
          params: {
            condensed: true,
          },
        },
      ],
      !!vars['tip'] && parseFloat(vars['tip']) > 0,
    ),
    ...conditionalLines(
      [
        {
          text: ' ',
          params: {
            fill: true,
          },
        },
        {
          text: `${type === ReceiptType.Refund ? '-' : ''}${(
            Number(vars['paymentIncTip']) / 100
          ).toFixed(2)}       ${i18n('Receipt.TotalIncTip')}`,
          params: {
            condensed: true,
          },
        },
      ],
      !!vars['tip'] && !!vars['paymentIncTip'] && parseFloat(vars['tip']) > 0,
    ),
    ...conditionalLines(
      [
        {
          text: 'Card Identifier: **** ${cardIdentifier}',
          params: {
            condensed: true,
          },
        },
      ],
      // Only show card identifier from backend if card details are not available
      !!vars['cardIdentifier'] && !cardDetails,
    ),
    ...conditionalLines(
      [
        {
          text: 'Payment Method: ${cardMethod}',
          params: {
            condensed: true,
          },
        },
      ],
      // Only show card method from backend if card details are not available
      !!vars['cardMethod'] && !cardDetails,
    ),
    // Card detail lines. Skipped entirely when the terminal gave us no usable values, so we
    // never print a block of blank lines.
    ...conditionalLines(
      generateCardDetailLines(cardDetails || {}, i18n),
      Boolean(
        cardDetails &&
          Object.entries(cardDetails).some(
            ([key, value]) => key !== '__typename' && Boolean(value),
          ),
      ),
    ),
  ];

  // If it is spain and it has a user the receipt title needs to be different.
  const receiptTextToUse =
    country === 'ES' && vars['userName']
      ? i18n('Receipt.ReceiptNo.UserConnection')
      : i18n('Receipt.ReceiptNo');

  return {
    varRegex: '\\$\\{([^}]*)\\}',
    vars: Object.keys(vars),
    lines: [
      ...conditionalLines(
        [
          {
            text: '#${orderNumber}',
            params: {
              doubleHeigth: true,
              align: 'CENTER',
            },
          },
        ],
        !!vars['orderNumber'],
      ),
      ...mapLines(introLines),
      {
        text: ' ',
        params: {
          fill: true,
        },
      },
      {
        text: ' ',
        params: {
          fill: true,
        },
      },
      ...conditionalLines(
        [
          {
            text: '${companyName}',
            params: {
              align: 'CENTER',
              condensed: true,
            },
          },
        ],
        vars['companyName'] !== vars['venueName'],
      ),
      {
        text: '${venueName}',
        params: {
          align: 'CENTER',
          condensed: true,
        },
      },
      {
        text: '${address}',
        params: {
          align: 'CENTER',
          condensed: true,
        },
      },
      {
        text: '${postalCode} ${city}',
        params: {
          align: 'CENTER',
          condensed: true,
        },
      },
      {
        text: `${i18n('Receipt.VATNo')}: \${orgNumber}`,
        params: {
          align: 'CENTER',
          condensed: true,
        },
      },
      {
        text: ' ',
        params: {
          fill: true,
        },
      },
      {
        text: receiptTitle,
        params: {
          doubleHeigth: true,
          align: receiptTitleAlign,
          fontSize: receiptTitleFontSize,
          condensed: isNorwayCheque ? true : undefined,
        },
      },
      // // For Norway, the prebill text is very long and needs
      ...conditionalLines(
        [
          {
            text: receiptTitlePartTwo,
            params: {
              doubleHeigth: true,
              align: receiptTitleAlign,
              fontSize: receiptTitleFontSize,
              condensed: isNorwayCheque ? true : undefined,
            },
          },
        ],
        !!isNorwayCheque,
      ),

      ...conditionalLines(
        [
          {
            text: '-',
            params: {
              fill: true,
              condensed: true,
            },
          },
          {
            text: vars['userName'],
            params: {
              align: 'CENTER',
              condensed: true,
            },
          },
          vars['userOrgNumber'] && {
            text: vars['userOrgNumber'],
            params: {
              align: 'CENTER',
              condensed: true,
            },
          },
          vars['userAddress'] && {
            text: vars['userAddress'],
            params: {
              align: 'CENTER',
              condensed: true,
            },
          },
          (vars['userZip'] || vars['userCity']) && {
            text: `${vars['userZip'] || ''} ${vars['userCity'] || ''}`.trim(),
            params: {
              align: 'CENTER',
              condensed: true,
            },
          },
          vars['userPhone'] && {
            text: vars['userPhone'],
            params: {
              align: 'CENTER',
              condensed: true,
            },
          },
        ].filter(Boolean),
        Boolean(country === 'ES' && vars['userName']),
      ),

      {
        text: '-',
        params: {
          fill: true,
          condensed: true,
        },
      },
      ...conditionalLines(
        [
          {
            text: `${receiptTextToUse}: \${checkId}`,
            params: {
              align: 'CENTER',
              condensed: true,
            },
          },
        ],
        !!vars['checkId'],
      ),
      ...conditionalLines(
        [
          {
            text: `${i18n('Receipt.RefundNumber')}: \${refundNumber}`,
            params: {
              align: 'CENTER',
              condensed: true,
            },
          },
        ],
        !!vars['refundNumber'],
      ),
      {
        text: '${datetime}',
        params: {
          align: 'CENTER',
          condensed: true,
        },
      },
      ...conditionalLines(
        [
          {
            text: `${i18n('Receipt.RefundReference')}: \${refundReference}`,
            params: {
              align: 'CENTER',
              condensed: true,
            },
          },
        ],
        !!vars['refundReference'],
      ),
      ...conditionalLines(
        [
          {
            text: `${i18n('Receipt.OrderDate')}: ` + '${orderDate}',
            params: {
              align: 'CENTER',
              condensed: true,
            },
          },
        ],
        !!vars['orderDate'],
      ),
      ...conditionalLines(
        [
          {
            text: '${easyId}',
            params: {
              align: 'CENTER',
              condensed: true,
            },
          },
        ],
        !!vars['easyId'],
      ),
      ...conditionalLines(
        [
          {
            text: `${i18n('Receipt.Table')}: \${tableCode} ${i18n(
              'Receipt.Coverage',
            )}: \${covers}`,
            params: {
              align: 'CENTER',
              condensed: true,
            },
          },
        ],
        !!isTable,
      ),
      {
        text: '-',
        params: {
          fill: true,
          condensed: true,
        },
      },

      // Middle section
      ...conditionalLines(...[middleSection], items.length > 0),
      ...conditionalLines(
        [
          {
            text: `${i18n('Receipt.TipRefunded')}: \${tipRefunded}`,
            params: {
              align: 'CENTER',
              condensed: true,
            },
          },
        ],
        type === ReceiptType.TipRefund,
      ),

      ...conditionalLines(...[paymentLines], !!payments),

      {
        text: '-',
        params: {
          fill: true,
          condensed: true,
        },
      },
      ...conditionalLines(
        [
          {
            text: 'Reg: ${deviceName}',
            params: {
              condensed: true,
              fontSize: 10,
            },
          },
        ],
        !!vars['deviceName'],
      ),
      ...conditionalLines(
        [
          {
            text: `${i18n('Receipt.ControlUnit')}: \${deviceId}`,
            params: {
              condensed: true,
              fontSize: 10,
            },
          },
          {
            text: ' ',
            params: {
              fill: true,
            },
          },
        ],
        !!vars['deviceId'],
      ),
      ...conditionalLines(
        [
          ...mapLines(bottomLines),
          {
            text: '-',
            params: {
              fill: true,
              condensed: true,
            },
          },
        ],
        !!bottomLines,
      ),

      // Invoice lines
      ...conditionalLines(generateInvoiceLines(totalAmount, i18n), isTypeInvoice),

      ...conditionalLines(
        [
          {
            text: ' ',
            params: {
              fill: true,
            },
          },
          {
            text: i18n(
              'Receipt.Staff',
              (type === ReceiptType.Sale ? staff?.name : vars.staffOnOrder) ?? '',
            ),
            params: {
              align: 'CENTER',
              condensed: true,
            },
          },
        ],
        !!showStaffName &&
          !!(type === ReceiptType.Sale ? staff?.name : vars.staffOnOrder) &&
          [ReceiptType.Sale, ReceiptType.Copy].includes(type),
      ),

      {
        text: ' ',
        params: {
          fill: true,
        },
      },
      {
        text: ' ',
        params: {
          fill: true,
        },
      },
    ],
  };
};
